import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../../components/Button';
import { ApiError } from '../../../services/api';
import { getAdminTours } from '../../../services/admin-tours';
import type { AdminTourFeature, AdminTourSummary } from '../../../types/adminTours';
import { TourBadge } from './TourBadge';
import { GetStartedChecklistSetting } from './GetStartedChecklistSetting';
import { TourVisibilitySetting } from './TourVisibilitySetting';
import { AdminPage, AdminPageHeader, AdminPanel } from '../AdminPage';
import './ToursSection.css';

type LoadState =
  | { kind: 'loading' }
  /** The API answered 404: this server predates the builder. Not an error worth alarming about. */
  | { kind: 'unavailable' }
  | { kind: 'failed'; message: string }
  | { kind: 'ready'; features: AdminTourFeature[] };

const KIND_LABELS: Record<AdminTourSummary['kind'], string> = {
  Page: 'Page tour',
  Data: 'Data tour',
  FlowStep: 'Flow step tour',
};

const describeError = (err: unknown, fallback: string): string =>
  err instanceof ApiError && err.message ? `${fallback} ${err.message}` : fallback;

const countLabel = (platform: string, { enabled, total }: { enabled: number; total: number }) =>
  `${platform} ${enabled}/${total}`;

const TourRow = ({ tour }: { tour: AdminTourSummary }) => (
  <li className="admin-tours__item">
    <Link
      to={`/admin/tours/${encodeURIComponent(tour.key)}`}
      className="admin-tours__row"
      data-testid={`tour-row-${tour.key}`}
    >
      <span className="admin-tours__row-main">
        <span className="admin-tours__name">{tour.name}</span>
        <span className="admin-tours__meta">
          {KIND_LABELS[tour.kind]} &middot; {tour.key}
        </span>
      </span>
      <span className="admin-tours__counts">
        <span className="admin-tours__count">{countLabel('Web', tour.counts.web)}</span>
        <span className="admin-tours__count">{countLabel('Mobile', tour.counts.mobile)}</span>
      </span>
      <span className="admin-tours__badges">
        {!tour.isEnabled && <TourBadge tone="muted">Disabled</TourBadge>}
        {tour.hasDraft && <TourBadge tone="draft">Draft</TourBadge>}
        {tour.newFromCodeCount > 0 && (
          <TourBadge tone="new">{tour.newFromCodeCount} new from code</TourBadge>
        )}
        {tour.removedFromCodeCount > 0 && (
          <TourBadge tone="removed">{tour.removedFromCodeCount} removed from code</TourBadge>
        )}
      </span>
    </Link>
  </li>
);

/**
 * Every guided tour, grouped by the feature it explains.
 *
 * The counts are per platform because a tour usually exists on both and is rarely identical on
 * each: "Web 4/5, Mobile 0/3" is the whole story of a tour that was never finished on mobile, and
 * it is the reason the list leads with numbers rather than a single on/off dot.
 */
export const ToursSection = () => {
  const [load, setLoad] = useState<LoadState>({ kind: 'loading' });
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    getAdminTours(controller.signal)
      .then((response) => setLoad({ kind: 'ready', features: response.features }))
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        if (err instanceof ApiError && err.statusCode === 404) {
          setLoad({ kind: 'unavailable' });
          return;
        }
        setLoad({ kind: 'failed', message: describeError(err, 'Could not load the tours.') });
      });
    return () => controller.abort();
  }, [reloadToken]);

  const handleRetry = () => {
    setLoad({ kind: 'loading' });
    setReloadToken((token) => token + 1);
  };

  return (
    <AdminPage labelledBy="admin-tours-title">
      <AdminPageHeader
        title="Tours"
        titleId="admin-tours-title"
        subtitle="The walkthroughs coaches see the first time they open a feature. Edit one as a draft, then publish it when it reads right."
      />

      <AdminPanel
        title="Onboarding"
        titleId="admin-onboarding-title"
        data-testid="admin-onboarding-settings"
      >
        <div className="admin-tours__settings">
          <TourVisibilitySetting />
          <GetStartedChecklistSetting />
        </div>
      </AdminPanel>

      {load.kind === 'loading' && <p className="admin-state">Loading the tours...</p>}

      {load.kind === 'unavailable' && (
        <p className="admin-state" data-testid="admin-tours-unavailable">
          The tour builder is not available on this server yet. It will show up here once the API
          update is deployed.
        </p>
      )}

      {load.kind === 'failed' && (
        <div className="admin-error" role="alert">
          <span>{load.message}</span>
          <Button type="button" variant="secondary" onClick={handleRetry} data-testid="admin-tours-retry">
            Try again
          </Button>
        </div>
      )}

      {load.kind === 'ready' && load.features.length === 0 && (
        <p className="admin-state" data-testid="admin-tours-empty">
          No tours have been defined yet.
        </p>
      )}

      {load.kind === 'ready' &&
        load.features.map((feature) => (
            <AdminPanel
              key={feature.key}
              title={feature.name}
              titleId={`admin-tours-feature-${feature.key}`}
              data-testid={`tour-feature-${feature.key}`}
            >
              <ul className="admin-tours__list">
                {feature.tours.map((tour) => (
                  <TourRow key={tour.key} tour={tour} />
                ))}
              </ul>
            </AdminPanel>
        ))}
    </AdminPage>
  );
};
